import axios from 'axios';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EntityState, serializeAxiosError, IQueryParams } from 'app/shared/reducers/reducer.utils';
import { IVirtualCoupling, VirtualCouplingResponse, defaultValue } from 'app/shared/model/virtual-coupling.model';

const initialState: EntityState<IVirtualCoupling> & { response: VirtualCouplingResponse | null } = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  response: null,
};

export interface VirtualCouplingProps extends IQueryParams {
  companyId?: number | null;
  page?: number | null;
  size?: number | null;
  sort?: string | null;
  licensePlate?: string | null;
}

const API_URL = 'api/virtual-couplings/';

// Actions

export const getVirtualCouplings = createAsyncThunk(
  'efficiency/fetch_virtual_couplings',
  async ({ companyId, page, size, sort, licensePlate }: VirtualCouplingProps) => {
    const companyIdParam = companyId ? `?searchCompanyId=${encodeURIComponent(companyId)}` : '';
    const apiUrlComplete = `${API_URL}${companyIdParam}`;
    const params = {
      ...(companyId !== null && { searchCompanyId: encodeURIComponent(companyId) }),
      page,
      size,
      sort,
      ...(licensePlate !== '' && { searchLicensePlate: encodeURIComponent(licensePlate) }),
    };
    const response = await axios.get<VirtualCouplingResponse>(API_URL, { params });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// Slice

const virtualCouplingSlice = createSlice({
  name: 'virtualCoupling',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getVirtualCouplings.fulfilled, (state, action) => {
        state.loading = false;
        state.response = action.payload; // <-- store the paginated response here
        state.entities = action.payload.content; // <-- update entities array with the content
      })
      .addCase(getVirtualCouplings.pending, state => {
        state.loading = true;
      })
      .addCase(getVirtualCouplings.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      });
  },
});

export const { reset } = virtualCouplingSlice.actions;

export default virtualCouplingSlice.reducer;
